import { AlertCircle, CheckCircle2, CloudOff, History } from "lucide-react"

import type { AutosaveStatus } from "@/hooks/use-autosave"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

export interface AutosaveFeedbackProps {
  status: AutosaveStatus
  onSave?: () => void
  onDiscard?: () => void
  className?: string
}

export function AutosaveFeedback({
  status,
  onSave,
  onDiscard,
  className,
}: AutosaveFeedbackProps) {
  if (status.phase === "idle" || status.phase === "discarded") return null

  const failed =
    status.phase === "error" || status.phase === "quota-exceeded"
  const content = {
    saved: ["Draft saved", "Your work is safely stored on this device."],
    restored: [
      "Draft restored",
      "Unsaved work from this device has been restored.",
    ],
    expired: ["Old draft removed", "The stored draft had expired."],
    corrupt: ["Unreadable draft removed", "The stored draft could not be used."],
    incompatible: [
      "Old draft removed",
      "The stored draft was created by an incompatible app version.",
    ],
    error: ["Draft not saved", "Local storage is unavailable on this device."],
    "quota-exceeded": [
      "Draft not saved",
      "This device does not have enough local storage space.",
    ],
  }[status.phase]

  if (!content) return null
  const Icon = failed
    ? CloudOff
    : status.phase === "saved"
      ? CheckCircle2
      : status.phase === "restored"
        ? History
        : AlertCircle

  return (
    <Alert
      variant={failed ? "destructive" : "default"}
      className={className}
      data-testid="autosave-feedback"
    >
      <Icon aria-hidden="true" />
      <AlertTitle>{content[0]}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
        <span>{content[1]}</span>
        <span className="flex gap-2">
          {failed && onSave && (
            <Button type="button" size="sm" variant="outline" onClick={onSave}>
              Try again
            </Button>
          )}
          {status.phase === "restored" && onDiscard && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onDiscard}
            >
              Discard draft
            </Button>
          )}
        </span>
      </AlertDescription>
    </Alert>
  )
}